import React, { useEffect, useState } from "react";
import { Box, Flex, Grid, Label, Spinner } from "theme-ui";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import TableBasic from "components/ui/Table";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment-timezone";
import { themevals } from "theme/themevals";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { EllipsisIcon, PlusIcon, PaymentsIcon, TrashIcon, QuestionMarkIcon, CheckMarkIcon } from "components/svg";
import BasicButton from "components/forms/BasicButton";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import SelectInput from "components/forms/SelectInput";
import TextInput from "components/forms/TextInput";
import BasicModal from "components/ui/BasicModal";
import { useToast } from "components/toast";
import { createPurchaseOrderPayment, deletePurchaseOrderPayment, getPurchaseOrderPayments, updatePurchaseOrderPayment } from "api/PurchaseOrder";

interface PaymentsProps {
  isCompany: boolean;
  po: any;
  reloadPO: () => void;
}

const manualPaymentOptions = [
  { label: "Cash", value: "CASH" },
  { label: "Check", value: "CHECK" },
  { label: "ACH", value: "ACH" },
];

const Payments: React.FC<PaymentsProps> = ({ isCompany, po, reloadPO }) => {
  const columnHelper = createColumnHelper<any>();
  const toast = useToast();
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Purchase Order Payments";
    _init();
  }, [po?.id]);

  const _init = async () => {
    if (!po?.id) return;
    setLoading(true);
    const res = await getPurchaseOrderPayments({ data: { id: po.id } });
    if (res?.items) {
      setPayments(res.items);
    } else if (Array.isArray(res)) {
      setPayments(res);
    } else {
      setPayments([]);
    }
    setLoading(false);
  };

  const columns = [
    columnHelper.accessor((row) => row?.transactionDate, {
      id: "transactionDate",
      header: () => <span>Transaction Date</span>,
      cell: (info) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row) => row?.value, {
      id: "amount",
      header: () => <span>Amount</span>,
      cell: (info) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.black }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00"}</Box>;
      },
    }),
    columnHelper.accessor((row) => row?.paymentMethod, {
      id: "method",
      header: () => <span>Method</span>,
      cell: (info) => info.getValue() ?? "-",
    }),
    columnHelper.accessor((row) => row?.transactionId, {
      id: "transactionId",
      header: () => <span>Transaction ID</span>,
      cell: (info) => info.getValue() ?? "-",
    }),
    columnHelper.accessor((row) => row?.reference, {
      id: "reference",
      header: () => <span>Reference</span>,
      cell: (info) => info.getValue() ?? "-",
    }),
    columnHelper.accessor((row) => row?.createdAt, {
      id: "createdAt",
      header: () => <span>Date Added</span>,
      cell: (info) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
    }),
    columnHelper.accessor((row) => `${row?.createdBy?.firstName ?? ""} ${row?.createdBy?.lastName ?? ""}`.trim(), {
      id: "createdBy",
      header: () => <span>Added By</span>,
      cell: (info) => info.getValue() || "-",
    }),
    ...(isCompany
      ? [
          columnHelper.accessor("actions", {
            id: "actions",
            header: () => null,
            enableSorting: false,
            meta: {
              style: { width: "10px" },
            },
            cell: (info) => {
              const paymentMethod = info.row.original.paymentMethod;
              const items = [
                { label: "Edit", onClick: () => _openEditPayment(info.row.original) },
                { label: "Delete", onClick: () => _confirmDeletePayment(info.row.original), style: { color: "var(--theme-ui-colors-danger)" } },
              ];

              if (paymentMethod === "Zoho_Payment") {
                items.shift(); // remove edit for synced payments
              }

              return (
                <Box>
                  <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
                </Box>
              );
            },
          }),
        ]
      : []),
  ];

  const _openCreateModal = () => {
    setIsCreateOpen(true);
  };

  const _closeCreateModal = () => {
    setIsCreateOpen(false);
  };

  const _openEditPayment = (payment: any) => {
    setSelectedPayment(payment);
    setIsEditOpen(true);
  };

  const _closeEditModal = () => {
    setIsEditOpen(false);
    setSelectedPayment(null);
  };

  const _confirmDeletePayment = (payment: any) => {
    setSelectedPayment(payment);
    setConfirmDeleteModalOpen(true);
  };

  const _cancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setSelectedPayment(null);
  };

  const _submitCreatePayment = async (values: any) => {
    if (!po?.id) return;
    setIsSubmitting(true);
    const payload = {
      paymentMethod: values.method,
      value: Number(values.amount),
      type: "PURCHASE_ORDER",
      reference: values.reference,
      transactionId: values.transactionId || undefined,
      transactionDate: values.transactionDate ? moment(values.transactionDate).toISOString() : moment().toISOString(),
    };

    const res = await createPurchaseOrderPayment({ id: po.id, data: payload });
    if (res?.id || res?.payment?.id) {
      toast.add("Payment added.", "var(--theme-ui-colors-success)");
      setIsCreateOpen(false);
      await Promise.all([_init(), reloadPO()]);
    }
    setIsSubmitting(false);
  };

  const _submitEditPayment = async (values: any) => {
    if (!po?.id || !selectedPayment?.id) return;
    setIsSubmitting(true);
    const updatePayload = {
      paymentMethod: values.method,
      value: Number(values.amount),
      reference: values.reference,
      transactionId: values.transactionId || undefined,
      transactionDate: values.transactionDate ? moment(values.transactionDate).toISOString() : null,
    };

    const res = await updatePurchaseOrderPayment({ id: po.id, paymentId: selectedPayment.id, data: updatePayload });
    if (res?.id || res?.payment?.id) {
      toast.add("Payment updated.", "var(--theme-ui-colors-success)");
      setIsEditOpen(false);
      setSelectedPayment(null);
      await Promise.all([_init(), reloadPO()]);
    }
    setIsSubmitting(false);
  };

  const _deletePayment = async () => {
    if (!po?.id || !selectedPayment?.id) return;
    setIsDeleting(true);
    const res = await deletePurchaseOrderPayment({ id: po.id, paymentId: selectedPayment.id });
    if (res?.success || res?.id) {
      toast.add("Payment deleted.", "var(--theme-ui-colors-success)");
      await Promise.all([_init(), reloadPO()]);
    }
    setIsDeleting(false);
    setConfirmDeleteModalOpen(false);
    setSelectedPayment(null);
  };

  const initialFormValues = {
    method: "CASH",
    amount: "",
    reference: "",
    transactionId: "",
    transactionDate: moment().format("YYYY-MM-DD"),
  };

  const validationSchema = Yup.object().shape({
    method: Yup.string().required("Method is required"),
    amount: Yup.number().typeError("Amount must be a number").required("Amount is required").min(0.01, "Amount must be at least 0.01"),
    reference: Yup.string().nullable(),
    transactionId: Yup.string().nullable(),
    transactionDate: Yup.date().nullable(),
  });

  const editInitialValues = selectedPayment
    ? {
        method: selectedPayment.paymentMethod || "CASH",
        amount: selectedPayment.value,
        reference: selectedPayment.reference || "",
        transactionId: selectedPayment.transactionId || "",
        transactionDate: selectedPayment.transactionDate ? moment(selectedPayment.transactionDate).format("YYYY-MM-DD") : "",
      }
    : initialFormValues;

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Payments
      </H5>
      {/* {isCompany && (
        <Flex sx={{ justifyContent: "flex-end" }}>
          <BasicButton scheme="secondaryOutline" onClick={_openCreateModal}>
            <PlusIcon fill="#000" />
            Add Payment
          </BasicButton>
        </Flex>
      )} */}
      {loading ? (
        <Flex sx={{ justifyContent: "center", padding: "40px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Flex>
      ) : payments.length === 0 ? (
        <Flex sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}>
          <Label sx={{ color: themevals.colors.dkgrey, fontSize: "14px", fontWeight: 500, fontFamily: "Poppins" }}>There are no payments on this Purchase Order.</Label>
        </Flex>
      ) : (
        <TableBasic data={payments} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} disableSorting />
      )}

      {/* Create Payment Modal */}
      {isCreateOpen && (
        <Modal size="md">
          <Formik initialValues={initialFormValues} validationSchema={validationSchema} onSubmit={_submitCreatePayment}>
            {({ handleSubmit, values, setFieldValue, errors, touched, submitCount }) => (
              <Form onSubmit={handleSubmit}>
                <ModalHeader>
                  <ModalTitleWrapper>
                    <ModalIcon>
                      <PaymentsIcon fill="000" />
                    </ModalIcon>
                    <Box>
                      <ModalTitle>Add Payment</ModalTitle>
                      <ModalSubtitle>Record a payment received for this purchase order.</ModalSubtitle>
                    </Box>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <Grid columns={[1, 2]} gap={20}>
                    <SelectInput
                      name="method"
                      label="Method"
                      value={values.method}
                      onChange={(val: any) => setFieldValue("method", val)}
                      options={manualPaymentOptions}
                      $errors={errors.method && submitCount > 0 ? errors.method : null}
                    />
                    <TextInput
                      name="amount"
                      type="number"
                      label="Amount"
                      placeholder="0.00"
                      value={values.amount}
                      onChange={(val: any) => setFieldValue("amount", val)}
                      $errors={errors.amount && submitCount > 0 ? errors.amount : null}
                    />
                    <TextInput name="reference" label="Reference" placeholder="Reference" value={values.reference} onChange={(val: any) => setFieldValue("reference", val)} />
                    <TextInput name="transactionId" label="Transaction ID" placeholder="Transaction ID" value={values.transactionId} onChange={(val: any) => setFieldValue("transactionId", val)} />
                    <TextInput name="transactionDate" label="Transaction Date" type="date" value={values.transactionDate} onChange={(val: any) => setFieldValue("transactionDate", val)} />
                  </Grid>
                </ModalBody>
                <ModalFooter>
                  <Flex sx={{ justifyContent: "flex-end", gap: "10px" }}>
                    <BasicButton scheme="secondary" type="button" onClick={_closeCreateModal}>
                      Cancel
                    </BasicButton>
                    <BasicButton type="submit" $submitting={isSubmitting} $iconWidth="14px" $iconHeight="14px" keepText={true} $loadingColor="#fff">
                      <CheckMarkIcon fill="#fff" />
                      Save Payment
                    </BasicButton>
                  </Flex>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </Modal>
      )}

      {/* Edit Payment Modal */}
      {isEditOpen && selectedPayment && (
        <Modal size="md">
          <Formik initialValues={editInitialValues} validationSchema={validationSchema} enableReinitialize onSubmit={_submitEditPayment}>
            {({ handleSubmit, values, setFieldValue, errors, touched, submitCount }) => (
              <Form onSubmit={handleSubmit}>
                <ModalHeader>
                  <ModalTitleWrapper>
                    <ModalIcon>
                      <PaymentsIcon fill="000" />
                    </ModalIcon>
                    <Box>
                      <ModalTitle>Edit Payment</ModalTitle>
                      <ModalSubtitle>Update the payment details.</ModalSubtitle>
                    </Box>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <Grid columns={[1, 2]} gap={20}>
                    <SelectInput
                      name="method"
                      label="Method"
                      value={values.method}
                      onChange={(val: any) => setFieldValue("method", val)}
                      options={manualPaymentOptions}
                      $errors={errors.method && submitCount > 0 ? errors.method : null}
                    />
                    <TextInput
                      name="amount"
                      type="number"
                      label="Amount"
                      placeholder="0.00"
                      value={values.amount}
                      onChange={(val: any) => setFieldValue("amount", val)}
                      $errors={errors.amount && submitCount > 0 ? errors.amount : null}
                    />
                    <TextInput name="reference" label="Reference" placeholder="Reference" value={values.reference} onChange={(val: any) => setFieldValue("reference", val)} />
                    <TextInput name="transactionId" label="Transaction ID" placeholder="Transaction ID" value={values.transactionId} onChange={(val: any) => setFieldValue("transactionId", val)} />
                    <TextInput name="transactionDate" label="Transaction Date" type="date" value={values.transactionDate} onChange={(val: any) => setFieldValue("transactionDate", val)} />
                  </Grid>
                </ModalBody>
                <ModalFooter>
                  <Flex sx={{ justifyContent: "flex-end", gap: "10px" }}>
                    <BasicButton scheme="secondary" type="button" onClick={_closeEditModal}>
                      Cancel
                    </BasicButton>
                    <BasicButton type="submit" $submitting={isSubmitting} $iconWidth="14px" $iconHeight="14px" keepText={true} $loadingColor="#fff">
                      <CheckMarkIcon fill="#fff" />
                      Update Payment
                    </BasicButton>
                  </Flex>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteModalOpen && selectedPayment && (
        <BasicModal
          title={`Delete payment?`}
          size="sm"
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          cancel_click={_cancelDelete}
          cancel_text="No, Cancel"
          $submitting={isDeleting}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={_deletePayment}
              $submitting={isDeleting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!isDeleting && <TrashIcon fill={"#fff"} />}
              Delete Payment
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to remove this payment?</P>
        </BasicModal>
      )}
    </>
  );
};

export default Payments;
